import { Icon, SkeletonLoader } from '@toggle/design-system';
import { Alert } from '@toggle/toggle';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useConversationTask } from '~/react-query-hooks/alfred/useConversationTask';
import { getCardDate } from '~/utils/card-utils/card-utils';

import sharedStyles from '../../AlertsCard.module.scss';
import { extractContentPreview } from '../../utils/extract-content.preview';
import { getErrorText } from '../../utils/get-error-text';
import styles from './AlertsAlfredCard.module.scss';

interface AlertsAlfredCardProps {
  alertEvent: Alert;
  dropdown?: React.ReactNode;
}

export const AlertsAlfredCard = ({
  alertEvent,
  dropdown,
}: AlertsAlfredCardProps) => {
  const { t } = useTranslation(['alerts', 'whatsNew'], {
    useSuspense: false,
  });
  const { created_at: createdAt, source_id: sourceId, read } = alertEvent;

  const { data: conversationData, isLoading } = useConversationTask(sourceId);
  const taskState = conversationData?.tasks?.[0]?.last_state;

  const isError =
    taskState?.status === 'Error' || taskState?.status === 'Fatal';

  if (isLoading) {
    return (
      <SkeletonLoader
        areas={[
          {
            styles: {
              width: '100%',
              height: '60px',
              borderRadius: '8px',
            },
          },
        ]}
      />
    );
  }

  if (!taskState) {
    return null;
  }

  return (
    <>
      <div className={sharedStyles.header}>
        <div className={sharedStyles.headerLeft}>
          <span>{t('alerts:alfredCard.title')}</span>
        </div>
        <div className={sharedStyles.headerRight}>
          <span className={sharedStyles.dateSpan}>
            {getCardDate(new Date(createdAt))}
          </span>
          {dropdown}
        </div>
      </div>
      <div className={sharedStyles.bodyWrapper}>
        <div className={sharedStyles.logoWrapper}>
          <div className={styles.alfredLogo}>
            <Icon iconName="Alfred" size={20} fillColor="var(--icon-primary)" />
          </div>
        </div>
        <div className={sharedStyles.textContent}>
          <p
            className={clsx(sharedStyles.primaryText, {
              [sharedStyles.read]: read,
            })}
          >
            {conversationData?.name}
          </p>
          <div className={styles.secondaryText}>
            {isError ? (
              <div className={styles.errorWrapper}>
                <Icon
                  fillColor={read ? 'var(--icon-soft)' : 'var(--icon-warning)'}
                  iconName="InformationCircle"
                  size={12}
                />
                <span
                  className={clsx(styles.errorText, {
                    [styles.read]: read,
                  })}
                >
                  {getErrorText(t, taskState?.content)}
                </span>
              </div>
            ) : (
              extractContentPreview(taskState?.content ?? '')
            )}
          </div>
        </div>
      </div>
    </>
  );
};
